"use client";

import { useState, useEffect, FunctionComponent, useRef, KeyboardEvent, PropsWithChildren, ReactElement, ReactNode, CSSProperties } from "react";
import ReactDOM from "react-dom";
import {
    Background,
    Body,
    Close,
    Container,
    ContainerInner,
    Content,
    Footer,
    Header,
    LoadingWrapper,
    Page,
    Pages,
    Sticky,
    Subtitle,
    Title,
    Wrapper,
} from "./modal.styles";
import { fixedContent } from "../utils/helper";
import { Close as CloseButton } from "../close/close";
import { Button, ButtonProps } from "../button/button";
import { InlineMessage, InlineMessageProps } from "../inline-message/inline-message";
import { Spinner } from "../spinner/spinner";
import { ScrollToProvider } from "../provider/scroll-to-provider";

export type ModalDictionary = {
    close?: string;
};

export interface ModalProps {
    /**
     * Whether the modal should be open or not.
     */
    open: boolean;

    /**
     * A callback function which will be fired on click to close icon/button.
     */
    onClose?: () => void;

    /**
     * Whether the close icon should be shown or not.
     */
    showCloseIcon?: boolean;

    /**
     * Hide background of the modal.
     */
    hideBackground?: boolean;

    /**
     * Whether the close button should be shown or not.
     */
    showCloseButton?: boolean;

    /**
     * A button which will appear in the footer. Will only appear if `footer` is not set.
     */
    button?: ButtonProps;

    /**
     * A button which will appear in the footer on the left side. Will only appear if `footer` is not set.
     */
    leftButton?: ButtonProps;

    /**
     * Title of the modal.
     */
    title?: ReactElement | string;

    /**
     * Subtitle of the modal.
     */
    subtitle?: ReactElement | string;

    /**
     * Whether the modal body should have padding or not.
     */
    padding?: boolean;

    /**
     * Whether the modal body should have margin or not.
     */
    margin?: boolean;

    /**
     * Lock screen on open modal.
     */
    lockScreen?: boolean;

    /**
     * Width of the modal.
     */
    width?: "xs" | "sm" | "lg" | "xl" | "2xl" | "full" | CSSProperties["width"];

    /**
     * Height of the modal.
     */
    height?: "auto" | "sm" | "lg" | "full" | CSSProperties["height"];

    /**
     * Border radius of the modal.
     */
    rounded?: "md" | "lg" | "xl";

    /**
     * CSS Selector where the modal should be rendered.
     */
    container?: string | Element | null;

    /**
     * Will display the modal with a transparent background.
     */
    transparent?: boolean;

    /**
     * Animation on open/close the modal.
     */
    animation?: "smooth";

    /**
     * Will render a custom close icon
     */
    closeIcon?: ReactNode | string;

    /**
     * Will render a fixed header with custom content. This will also override `title` and `subtitle`
     */
    header?: ReactNode | string;

    /**
     * Will render a sticky box with custom content.
     */
    sticky?: ReactNode | string;

    /**
     * Will render a fixed footer with custom content.
     */
    footer?: ReactNode | string;

    /**
     * Dictionary of the modal.
     */
    dictionary?: ModalDictionary;

    /**
     * Will display a loading spinner.
     */
    loading?: boolean | false;

    /**
     * Will remove padding and border between header, content and footer
     */
    flat?: boolean;

    /**
     * Will render a error message.
     */
    error?: InlineMessageProps | undefined;

    /**
     * Class name of the modal.
     */
    className?: string;
}

export const Modal: FunctionComponent<PropsWithChildren<ModalProps>> = ({
    open = false,
    onClose,
    showCloseIcon = true,
    showCloseButton = true,
    button,
    leftButton,
    lockScreen = true,
    title,
    closeIcon,
    subtitle,
    padding = true,
    margin = true,
    width,
    container,
    height = "auto",
    transparent,
    rounded,
    loading,
    flat = false,
    error,
    header,
    sticky,
    footer,
    animation,
    hideBackground,
    dictionary,
    className,
    children,
}) => {
    const modal = useRef<HTMLDivElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [loaded, setLoaded] = useState<boolean>(false);
    const firstFocusableElementRef = useRef<HTMLElement | null>(null);
    const lastFocusableElementRef = useRef<HTMLElement | null>(null);
    const [key, setKey] = useState<string>("modal");
    const [lastClickedButton, setLastClickedButton] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setLoaded(true);
        const buttons = document.querySelectorAll("button, [href]");

        buttons.forEach((button) => {
            button.addEventListener("click", handleButtonClick);
        });

        return function cleanup() {
            buttons.forEach((button) => {
                button.removeEventListener("click", handleButtonClick);
            });
        };
    }, []);

    useEffect(() => {
        if (open) {
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current);
            }
            setIsOpen(true);
            if (lockScreen && !document.body.classList.contains("site-fixed")) {
                fixedContent(true, "true");
            }
            setTimeout(() => {
                if (modal.current) {
                    setIsOpenModal(true);
                    modal.current.focus();
                    const focusableElements = modal.current.querySelectorAll(
                        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                    );
                    if (focusableElements.length > 0) {
                        const firstElement = focusableElements[0];
                        const lastElement = focusableElements[focusableElements.length - 1];
                        if (firstElement instanceof HTMLElement && lastElement instanceof HTMLElement) {
                            firstFocusableElementRef.current = firstElement;
                            lastFocusableElementRef.current = lastElement;
                            firstFocusableElementRef.current.focus();
                        }
                    }
                }
            }, 10);
        } else {
            closeTimeout.current = setTimeout(() => {
                handleClose();
            }, 310);
        }

        return function cleanup() {
            if (closeTimeout.current) {
                clearTimeout(closeTimeout.current);
            }
        };
    }, [open]);

    function handleButtonClick(event: any) {
        if (!open && !loaded) {
            setLastClickedButton(event.target as HTMLButtonElement | null);
        }
    }

    function handleClose() {
        setIsOpenModal(false);
        const shouldUnlock = document.body.classList.contains("site-fixed");

        setTimeout(() => {
            if (shouldUnlock && lockScreen) {
                fixedContent(false, "false");
            }
            setKey("close-modal");
            if (lastClickedButton) {
                lastClickedButton.focus();
            }
            setIsOpen(false);
            onClose?.();
        }, 310);
    }

    function handleKeyDown(event: KeyboardEvent<HTMLElement>) {
        if (event.key === "Tab" && !event.shiftKey) {
            if (firstFocusableElementRef.current && document.activeElement === lastFocusableElementRef.current) {
                event.preventDefault();
                firstFocusableElementRef.current.focus();
            }
        } else if (event.key === "Tab" && event.shiftKey) {
            if (lastFocusableElementRef.current && document.activeElement === firstFocusableElementRef.current) {
                event.preventDefault();
                lastFocusableElementRef.current.focus();
            }
        }
    }

    return isOpen
        ? ReactDOM.createPortal(
              <Wrapper
                  role="presentation"
                  onKeyDown={handleKeyDown}
                  open={isOpenModal}
                  padding={loading || error !== undefined ? true : padding}
                  animation={animation}
                  rounded={rounded}
                  key={key}
                  lockScreen={lockScreen}
                  ref={modal}
                  className={className}
              >
                  <ScrollToProvider scrollableRef={bodyRef}>
                      <Container tabIndex={-1} margin={margin}>
                          <ContainerInner height={height} margin={margin}>
                              <Content width={width} animation={animation} transparent={transparent}>
                                  {showCloseIcon && <Close>{closeIcon ? closeIcon : <CloseButton onClick={handleClose} color="gray" />}</Close>}
                                  <Pages>
                                      <Page height={height}>
                                          {header ? (
                                              <Header custom={true}>{header}</Header>
                                          ) : (
                                              <>
                                                  {(title || subtitle) && (
                                                      <Header flat={flat}>
                                                          {title && <Title>{title}</Title>}
                                                          {subtitle && <Subtitle>{subtitle}</Subtitle>}
                                                      </Header>
                                                  )}
                                              </>
                                          )}
                                          {sticky && <Sticky>{sticky}</Sticky>}
                                          <Body height={height} ref={bodyRef}>
                                              {loading ? (
                                                  <LoadingWrapper>
                                                      <Spinner size="md" />
                                                  </LoadingWrapper>
                                              ) : error ? (
                                                  <InlineMessage color="red" type="error" {...error} />
                                              ) : (
                                                  children
                                              )}
                                          </Body>
                                          {footer ? (
                                              <Footer custom={true}>{footer}</Footer>
                                          ) : (
                                              <>
                                                  {(showCloseButton || button || leftButton) && (
                                                      <Footer flat={flat}>
                                                          {leftButton && (
                                                              <div className="mr-auto">
                                                                  <Button size="sm" rounded="full" {...leftButton} />
                                                              </div>
                                                          )}
                                                          {showCloseButton && (
                                                              <div>
                                                                  <Button
                                                                      size="sm"
                                                                      rounded="full"
                                                                      label={dictionary?.close || "Close"}
                                                                      onClick={handleClose}
                                                                  />
                                                              </div>
                                                          )}
                                                          {button && (
                                                              <div>
                                                                  <Button size="sm" rounded="full" {...button} />
                                                              </div>
                                                          )}
                                                      </Footer>
                                                  )}
                                              </>
                                          )}
                                      </Page>
                                  </Pages>
                              </Content>
                          </ContainerInner>
                      </Container>
                  </ScrollToProvider>
                  {hideBackground !== true && <Background aria-hidden="true"></Background>}
              </Wrapper>,
              container ? (typeof container === "string" ? document.querySelector(container) : container) || document.body : document.body,
          )
        : null;
};
